import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import iconArrowLeftBlue from "../../../../img/icons/arrow_left_blue.svg";
import iconArrowRightBlue from "../../../../img/icons/arrow_right_blue.svg";
import iconCarouselArrowLeft from "../../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../../img/icons/carousel-arrow-right.svg";
import { Image } from "../../../common/Image";
import { RawHTML } from "../../../common/RawHTML";
import { IImageURL, IProductID, isoImageURL } from "../../../models/nominals";
import { BreakPoint, IViewport } from "../../../models/screen";
import { usePageSetting } from "../../../utils/settings";
import { IGallery, ISlide } from "../models";

import styles from "./gallery.module.scss";

interface IProps {
    gallery: IGallery;
    defaultThumbnail: IImageURL;
    viewport: IViewport;
}

enum GalleryCaption {
    SEALY_BLUE = "gallery-caption--sealy-blue",
    SEALY_LIME_YELLOW = "gallery-caption--sealy-lime-yellow",
    ORIENT = "gallery-caption--orient",
    RAZZMATAZZ = "gallery-caption--razzmatazz",
    YELLOW = "gallery-caption--yellow",
    TEAL = "gallery-caption--teal",
}

enum GalleryTextAlignment {
    CENTER = "gallery-caption--text-center",
    RIGHT = "gallery-caption--text-right",
}

const calculateThumbnailVisibility = (
    container: HTMLDivElement | null,
    thumbnailRefs: React.RefObject<HTMLButtonElement>[],
): boolean[] => {
    if (!container) return [];
    return thumbnailRefs.map((ref) => {
        if (!ref.current) return false;
        const thumbRect = ref.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return (
            thumbRect.left >= containerRect.left &&
            thumbRect.right <= containerRect.right
        );
    });
};

const areBooleanArraysEqual = (a: boolean[], b: boolean[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((value, index) => value === b[index]);
};

const scrollCurrentThumbnailIntoView = (
    container: HTMLDivElement | null,
    innerDiv: HTMLDivElement | null,
    currentSlideIndex: number,
): void => {
    if (!container || !innerDiv) return;
    const currentImage = innerDiv.children[currentSlideIndex];

    if (currentImage) {
        const containerRect = container.getBoundingClientRect();
        const imageRect = currentImage.getBoundingClientRect();

        // Check if the image is outside the container's left or right boundaries
        if (
            imageRect.left < containerRect.left ||
            imageRect.right > containerRect.right
        ) {
            // Scroll the image into view
            currentImage.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
            });
        }
    }
};

const getSlideImageLazyLoadSetting = (
    appSlug: string | null | undefined,
    lazy: boolean,
): boolean => {
    if (appSlug === "cocoon") {
        return false;
    }
    if (appSlug === "tempurpedic") {
        return lazy;
    }
    return true;
};

const renderSlideContent = (
    slide: ISlide,
    appSlug: string | null | undefined,
    lazy: boolean,
): React.ReactNode => {
    const captionClasses = classNames({
        [styles.galleryCaption]: !slide.caption_style,
        [styles.galleryCaptionSealyBlue]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_BLUE,
        [styles.galleryCaptionSealyLimeYellow]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_LIME_YELLOW,
        [styles.galleryCaptionOrient]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.ORIENT,
        [styles.galleryCaptionRazzmatazz]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.RAZZMATAZZ,
        [styles.galleryCaptionYellow]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.YELLOW,
        [styles.galleryCaptionTeal]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.TEAL,
        [styles.galleryCaptionTextCenter]:
            !!slide.caption_text_alignment &&
            slide.caption_text_alignment === GalleryTextAlignment.CENTER,
        [styles.galleryCaptionTextRight]:
            !!slide.caption_text_alignment &&
            slide.caption_text_alignment === GalleryTextAlignment.RIGHT,
    });
    const roundedCorner = classNames({
        [styles.galleryRoundedCorner]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_LIME_YELLOW,
    });
    return slide.content.map((contentBlock) => {
        if (contentBlock.type === "video_block") {
            return (
                <RawHTML
                    key={contentBlock.id}
                    className={styles.videoContainer}
                    html={contentBlock.value}
                />
            );
        }

        if (!contentBlock.value) {
            return null;
        }

        return (
            <figure key={contentBlock.id}>
                <picture className={roundedCorner}>
                    {contentBlock.value.renditions?.mobile_img && (
                        <source
                            media={`(max-width: ${contentBlock.value.renditions.mobile_img.width}px)`}
                            srcSet={isoImageURL.unwrap(
                                contentBlock.value.renditions.mobile_img.url,
                            )}
                        />
                    )}
                    <Image
                        src={contentBlock.value.url}
                        alt={contentBlock.value.title}
                        lazy={getSlideImageLazyLoadSetting(appSlug, lazy)}
                    />
                </picture>
                {slide.caption && (
                    <figcaption className={captionClasses}>
                        {slide.caption}
                    </figcaption>
                )}
            </figure>
        );
    });
};

export const Gallery = React.memo((props: IProps): React.ReactNode => {
    const appSlug = usePageSetting("app_slug");

    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [currentProduct, setCurrentProduct] =
        React.useState<IProductID | null>(props.gallery.product);
    const [lazy, setLazy] = React.useState(false);
    const [visibleThumbs, setVisibleThumbs] = React.useState<boolean[]>([]);
    const visibleThumbsRef = React.useRef<boolean[]>([]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const innerRef = React.useRef<HTMLDivElement>(null);
    const thumbnailRefsRef = React.useRef<React.RefObject<HTMLButtonElement>[]>(
        [],
    );

    // Initialize thumbnail refs array
    React.useEffect(() => {
        thumbnailRefsRef.current = props.gallery.gallery.collection.map(
            (_, i) => thumbnailRefsRef.current[i] || React.createRef(),
        );
    }, [props.gallery.gallery.collection]);

    const isMediumScreenWidth = props.viewport.width <= BreakPoint.MEDIUM;
    const document = getDocument();
    const isSmallScreenWidth =
        Math.min(
            props.viewport.width,
            document?.documentElement.clientWidth ?? Infinity,
        ) <= BreakPoint.SMALL;

    // Reset gallery when product changes
    React.useEffect(() => {
        if (props.gallery.product !== currentProduct) {
            setCurrentSlide(0);
            setCurrentProduct(props.gallery.product);
        }
    }, [props.gallery.product, currentProduct]);

    // Required: Event listener needs stable reference for proper cleanup.
    // Note: visibleThumbs excluded from deps to prevent infinite loop - manual comparison used instead.
    const updateThumbnailVisibility = React.useCallback(() => {
        const visibilities = calculateThumbnailVisibility(
            containerRef.current,
            thumbnailRefsRef.current,
        );
        if (!areBooleanArraysEqual(visibleThumbsRef.current, visibilities)) {
            visibleThumbsRef.current = visibilities;
            setVisibleThumbs(visibilities);
        }
    }, []);

    // Set up event listeners
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }
        updateThumbnailVisibility();
        window.addEventListener("resize", updateThumbnailVisibility);
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", updateThumbnailVisibility);
        }

        return () => {
            window.removeEventListener("resize", updateThumbnailVisibility);
            if (container) {
                container.removeEventListener(
                    "scroll",
                    updateThumbnailVisibility,
                );
            }
        };
    }, [updateThumbnailVisibility]);

    // Mirror the legacy componentDidUpdate call that re-evaluated visibility after every render
    React.useEffect(() => {
        updateThumbnailVisibility();
    });

    const updateSlide = (slideIdx: number) => {
        setCurrentSlide(slideIdx);
        setLazy(true);
        // Schedule scroll after state update
        setTimeout(() => {
            scrollCurrentThumbnailIntoView(
                containerRef.current,
                innerRef.current,
                slideIdx,
            );
        }, 0);
    };

    const prevSlide = () => {
        const newSlide =
            currentSlide <= 0
                ? props.gallery.gallery.collection.length - 1
                : currentSlide - 1;
        setCurrentSlide(newSlide);
        setLazy(true);
        // Schedule scroll after state update
        setTimeout(() => {
            scrollCurrentThumbnailIntoView(
                containerRef.current,
                innerRef.current,
                newSlide,
            );
        }, 0);
    };

    const nextSlide = () => {
        const newSlide =
            currentSlide >= props.gallery.gallery.collection.length - 1
                ? 0
                : currentSlide + 1;
        setCurrentSlide(newSlide);
        setLazy(true);
        // Schedule scroll after state update
        setTimeout(() => {
            scrollCurrentThumbnailIntoView(
                containerRef.current,
                innerRef.current,
                newSlide,
            );
        }, 0);
    };

    const currentSlideData =
        props.gallery.gallery.collection[currentSlide]?.value;

    if (!currentSlideData) {
        return null;
    }

    return (
        <span className={styles.root}>
            {isSmallScreenWidth && (
                <div className={styles.slideCount}>
                    {interpolate(t`%(idx)s/%(total)s`, {
                        idx: `${currentSlide + 1}`,
                        total: `${props.gallery.gallery.collection.length}`,
                    })}
                </div>
            )}
            {isMediumScreenWidth && (
                <div className={styles.slideButtons}>
                    <button onClick={prevSlide} aria-label={t`Previous`}>
                        <SVG aria-hidden="true" src={iconCarouselArrowLeft} />
                    </button>
                </div>
            )}

            {renderSlideContent(currentSlideData, appSlug, lazy)}

            {isMediumScreenWidth && (
                <div className={styles.slideButtons}>
                    <button onClick={nextSlide} aria-label={t`Next`}>
                        <SVG aria-hidden="true" src={iconCarouselArrowRight} />
                    </button>
                </div>
            )}
            {!isSmallScreenWidth && (
                <nav>
                    <button
                        className={styles.navButton}
                        onClick={prevSlide}
                        aria-label={t`Previous`}
                    >
                        <SVG aria-hidden="true" src={iconArrowLeftBlue} />
                    </button>
                    <div className={styles.previews} ref={containerRef}>
                        <div className={styles.inner} ref={innerRef}>
                            {props.gallery.gallery.collection.map(
                                (slide, i) => {
                                    const slideNum = i + 1;
                                    // TODO(i18n): Use t tagged template with interpolation: t`Slide ${slideNum}`
                                    const label = `Slide ${slideNum}`;
                                    const firstContentBlock =
                                        slide.value.content[0];
                                    if (!firstContentBlock.value) {
                                        return null;
                                    }
                                    const thumbnailURL =
                                        firstContentBlock &&
                                        firstContentBlock.type === "image"
                                            ? firstContentBlock.value.renditions
                                                  ?.thumbnail?.url
                                            : slide.value.video_thumbnail
                                                  ?.renditions?.thumbnail?.url;
                                    const isCurrentThumbnail =
                                        i === currentSlide;
                                    const isVisible =
                                        visibleThumbs[i] !== false;
                                    return (
                                        <button
                                            key={i}
                                            ref={thumbnailRefsRef.current[i]}
                                            className={styles.previewButton}
                                            onClick={() => {
                                                updateSlide(i);
                                            }}
                                            aria-current={isCurrentThumbnail}
                                            aria-hidden={!isVisible}
                                            tabIndex={isVisible ? 0 : -1}
                                        >
                                            <img
                                                src={isoImageURL.unwrap(
                                                    thumbnailURL ||
                                                        props.defaultThumbnail,
                                                )}
                                                alt={label}
                                            />
                                        </button>
                                    );
                                },
                            )}
                        </div>
                    </div>
                    <button
                        className={styles.navButton}
                        onClick={nextSlide}
                        aria-label={t`Next`}
                    >
                        <SVG aria-hidden="true" src={iconArrowRightBlue} />
                    </button>
                </nav>
            )}
        </span>
    );
});
